import { useMemo } from 'react';
import ReactSelect from 'react-select';

import type {
  LayoutCard,
  RangeValue
} from '@lib/transfers/store/custom-transfers-slice';
import {
  useCustomTransfersStore,
  type PlayerInItem
} from '@lib/transfers/store/useCustomTransfersStore';
import { usePlayerSearch } from '@hooks/transfers/usePlayerSearch';
import Icon, { IconType } from '@components/atoms/icon';
import Slider from '@components/atoms/slider';
import { BodyRegular, BodyRegularBold } from '@components/atoms/typography';
import { useTeams } from '@hooks/useTeams';
import type {
  PlannerSquadPlayer,
  PlannerBenchPlayer
} from '@api-service/planner';
import type { PlayerPosition } from '@api-service/players';

const POSITIONS: { label: string; value: PlayerPosition }[] = [
  { label: 'GK', value: 'GK' },
  { label: 'DEF', value: 'DEF' },
  { label: 'MID', value: 'MID' },
  { label: 'FWD', value: 'FWD' }
];

type CardShellProps = {
  iconName: IconType;
  label: string;
  onDelete: () => void;
  children: React.ReactNode;
  dataCy: string;
};

const CardShell = ({
  iconName,
  label,
  onDelete,
  children,
  dataCy
}: CardShellProps) => (
  <div className="flex items-center" data-cy={dataCy}>
    <div className="flex flex-col md:flex-row p-3 rounded-lg shadow-300 w-full justify-between items-start md:items-center">
      <div className="flex items-center mb-3 md:mb-0">
        <Icon name={iconName} className="text-neutral-600 w-5 h-5 mr-2" />
        <BodyRegularBold className="w-[140px] mb-0">{label}</BodyRegularBold>
      </div>
      <div className="w-full">{children}</div>
    </div>
    <Icon
      name="Delete"
      className="ml-4 text-danger-600 w-5 h-5 pointer cursor-pointer"
      onClick={onDelete}
    />
  </div>
);

const NoOfTransfersCard = ({ onDelete }: { onDelete: () => void }) => {
  const noOfTransfers = useCustomTransfersStore((state) => state.noOfTransfers);
  const setNoOfTransfers = useCustomTransfersStore(
    (state) => state.setNoOfTransfers
  );

  return (
    <CardShell
      iconName="Number"
      label="No. of transfers"
      onDelete={onDelete}
      dataCy="filter-no-of-transfers"
    >
      <Slider
        variant="uncontrolled"
        name="no-of-transfers-range-slider"
        min={1}
        max={8}
        step={1}
        defaultValue={[noOfTransfers.min, noOfTransfers.max]}
        onValueCommit={(value) => {
          const [min, max] = Array.isArray(value) ? value : [value, value];
          setNoOfTransfers(min, max);
        }}
        fixedLabels
      />
    </CardShell>
  );
};

const PriceRangeCard = ({ onDelete }: { onDelete: () => void }) => {
  const playerPrice = useCustomTransfersStore((state) => state.playerPrice);
  const setPlayerPrice = useCustomTransfersStore(
    (state) => state.setPlayerPrice
  );

  return (
    <CardShell
      iconName="Currency"
      label="Player price"
      onDelete={onDelete}
      dataCy="filter-player-price"
    >
      <Slider
        variant="uncontrolled"
        name="player-price-range-slider"
        min={4}
        max={15}
        step={0.1}
        defaultValue={[playerPrice.min, playerPrice.max]}
        onValueCommit={(value) => {
          const [min, max] = Array.isArray(value) ? value : [value, value];
          setPlayerPrice(min, max);
        }}
        fixedLabels
      />
    </CardShell>
  );
};

const OwnershipRangeCard = ({ onDelete }: { onDelete: () => void }) => {
  const effectiveOwnership = useCustomTransfersStore(
    (state) => state.effectiveOwnership
  );
  const setEffectiveOwnership = useCustomTransfersStore(
    (state) => state.setEffectiveOwnership
  );

  return (
    <CardShell
      iconName="PersonStar"
      label="Effective ownership"
      onDelete={onDelete}
      dataCy="filter-effective-ownership"
    >
      <Slider
        variant="uncontrolled"
        name="ownership-range-slider"
        min={0}
        max={100}
        step={1}
        defaultValue={[effectiveOwnership.min, effectiveOwnership.max]}
        onValueCommit={(value) => {
          const [min, max] = Array.isArray(value) ? value : [value, value];
          setEffectiveOwnership(min, max);
        }}
        fixedLabels
      />
    </CardShell>
  );
};

const PositionCard = ({ onDelete }: { onDelete: () => void }) => {
  const playerPosition = useCustomTransfersStore(
    (state) => state.playerPosition
  );
  const setPlayerPosition = useCustomTransfersStore(
    (state) => state.setPlayerPosition
  );

  return (
    <CardShell
      iconName="PersonRunningFilled"
      label="Position"
      onDelete={onDelete}
      dataCy="filter-player-position"
    >
      <div className="flex gap-2">
        {POSITIONS.map(({ label, value }) => {
          const isSelected = playerPosition === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setPlayerPosition(isSelected ? null : value)}
              className={`rounded-lg py-2 w-[64px] md:w-[94px] ${
                isSelected ? 'bg-primary-600 text-white' : 'bg-neutral-100'
              }`}
              data-cy={`position-option-${value}`}
            >
              <BodyRegularBold
                className="mb-0"
                textColor={isSelected ? 'text-white' : 'text-neutral-800'}
              >
                {label}
              </BodyRegularBold>
            </button>
          );
        })}
      </div>
    </CardShell>
  );
};

const TeamCard = ({ onDelete }: { onDelete: () => void }) => {
  const teamIds = useCustomTransfersStore((state) => state.teamIds);
  const setTeamIds = useCustomTransfersStore((state) => state.setTeamIds);
  const { teams } = useTeams();

  const options = useMemo(
    () =>
      teams
        .map((team) => ({ value: team.id, label: team.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teams]
  );

  const value = options.filter((option) => teamIds.includes(option.value));

  return (
    <CardShell
      iconName="ContactCard"
      label="Team"
      onDelete={onDelete}
      dataCy="filter-team"
    >
      <ReactSelect
        isMulti
        isSearchable={false}
        menuPlacement="auto"
        placeholder="Choose teams"
        options={options}
        value={value}
        onChange={(selected) =>
          setTeamIds((selected ?? []).map((option) => option.value))
        }
      />
    </CardShell>
  );
};

type PlayerOption = {
  value: string;
  label: string;
  team: string;
};

const PlayerInCard = ({ onDelete }: { onDelete: () => void }) => {
  const playerInItems = useCustomTransfersStore((state) => state.playerInItems);
  const setPlayerInItems = useCustomTransfersStore(
    (state) => state.setPlayerInItems
  );
  const { searchItems, onQueryChange } = usePlayerSearch();

  const value: PlayerOption[] = playerInItems.map((item) => ({
    value: item.value,
    label: item.label,
    team: item.team
  }));

  return (
    <CardShell
      iconName="PlayerIn"
      label="Player in"
      onDelete={onDelete}
      dataCy="filter-player-in"
    >
      <ReactSelect
        isMulti
        isSearchable
        menuPlacement="auto"
        placeholder="Search for a player"
        options={searchItems}
        value={value}
        onInputChange={(input) => onQueryChange(input)}
        onChange={(selected) =>
          setPlayerInItems(
            (selected ?? []).map(
              (option): PlayerInItem => ({
                value: option.value,
                label: option.label,
                team: option.team
              })
            )
          )
        }
        formatOptionLabel={(option) => (
          <span>
            {option.label}{' '}
            <span className="text-neutral-500">({option.team})</span>
          </span>
        )}
        filterOption={null}
      />
    </CardShell>
  );
};

type PlayerOutCardProps = {
  onDelete: () => void;
  squad: (PlannerSquadPlayer | PlannerBenchPlayer)[];
};

const PlayerOutCard = ({ onDelete, squad }: PlayerOutCardProps) => {
  const playerOutIds = useCustomTransfersStore((state) => state.playerOutIds);
  const setPlayerOutIds = useCustomTransfersStore(
    (state) => state.setPlayerOutIds
  );

  const options = squad.map((player) => ({
    value: player.id,
    label: player.displayName,
    team: player.team.shortName
  }));

  const value = options.filter((option) => playerOutIds.includes(option.value));

  return (
    <CardShell
      iconName="PlayerOut"
      label="Player out"
      onDelete={onDelete}
      dataCy="filter-player-out"
    >
      <ReactSelect
        isMulti
        isSearchable
        menuPlacement="auto"
        placeholder="Choose players from your squad"
        options={options}
        value={value}
        onChange={(selected) =>
          setPlayerOutIds((selected ?? []).map((option) => option.value))
        }
        formatOptionLabel={(option) => (
          <span>
            {option.label}{' '}
            <span className="text-neutral-500">({option.team})</span>
          </span>
        )}
      />
    </CardShell>
  );
};

type CustomFilterCardsProps = {
  squad: (PlannerSquadPlayer | PlannerBenchPlayer)[];
};

export const CustomFilterCards = ({ squad }: CustomFilterCardsProps) => {
  const layoutOrder = useCustomTransfersStore((state) => state.layoutOrder);
  const removeLayoutCard = useCustomTransfersStore(
    (state) => state.removeLayoutCard
  );

  if (layoutOrder.length === 0) {
    return (
      <BodyRegular className="mt-4 mb-0 text-neutral-600">
        Add filters above to start customising transfer recommendations.
      </BodyRegular>
    );
  }

  return (
    <div className="flex flex-col gap-3 mt-4">
      {layoutOrder.map((card) => {
        const onDelete = () => removeLayoutCard(card);
        switch (card) {
          case 'noOfTransfers':
            return <NoOfTransfersCard key={card} onDelete={onDelete} />;
          case 'playerPosition':
            return <PositionCard key={card} onDelete={onDelete} />;
          case 'team':
            return <TeamCard key={card} onDelete={onDelete} />;
          case 'effectiveOwnership':
            return <OwnershipRangeCard key={card} onDelete={onDelete} />;
          case 'playerPrice':
            return <PriceRangeCard key={card} onDelete={onDelete} />;
          case 'playerIn':
            return <PlayerInCard key={card} onDelete={onDelete} />;
          case 'playerOut':
            return (
              <PlayerOutCard key={card} onDelete={onDelete} squad={squad} />
            );
        }
      })}
    </div>
  );
};

export const FILTER_OPTIONS: {
  card: LayoutCard;
  label: string;
  iconName: IconType;
}[] = [
  { card: 'noOfTransfers', label: 'No. of transfers', iconName: 'Number' },
  {
    card: 'playerPosition',
    label: 'Position',
    iconName: 'PersonRunningFilled'
  },
  { card: 'team', label: 'Team', iconName: 'ContactCard' },
  {
    card: 'effectiveOwnership',
    label: 'Effective ownership',
    iconName: 'PersonStar'
  },
  { card: 'playerPrice', label: 'Player price', iconName: 'Currency' },
  { card: 'playerIn', label: 'Player in', iconName: 'PlayerIn' },
  { card: 'playerOut', label: 'Player out', iconName: 'PlayerOut' }
];

export type { LayoutCard, RangeValue };
